import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import ReviewCard from '../../components/admin/ReviewCard';
import UploadModal from '../../components/admin/UploadModal';

interface IntelligenceSource {
    id: string;
    original_url: string;
    source_type: 'TRADE' | 'TENDER' | 'SUPPLY_DEMAND' | 'NEWS';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    confidence_score: number;
    is_focus: boolean;
    ai_analysis_result: any;
    created_at: string;
}

const IntelligenceRoom = () => {
    // 状态管理
    const [reviews, setReviews] = useState<IntelligenceSource[]>([]); // 审核数据列表
    const [loading, setLoading] = useState(true); // 加载状态
    const [loadingTrigger, setLoadingTrigger] = useState(false); // 触发采集按钮加载状态
    const [filterFocus, setFilterFocus] = useState(false); // 是否只看承德专线
    const [statusFilter, setStatusFilter] = useState('PENDING'); // 状态筛选，默认"待审核"
    const [uploadModalVisible, setUploadModalVisible] = useState(false); // 上传弹窗可见性

    // 获取审核数据列表
    const fetchReviews = async () => {
        setLoading(true);
        try {
            const url = new URL('/api/v1/intelligence/reviews', window.location.origin);
            url.searchParams.append('status', statusFilter);
            if (filterFocus) {
                url.searchParams.append('is_focus', 'true');
            }


            const res = await fetch(url.toString(), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                // 按照可信度降序排序 (高分在前)
                data.sort((a: IntelligenceSource, b: IntelligenceSource) => b.confidence_score - a.confidence_score);
                setReviews(data);
            }
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    // 手动触发采集任务
    const handleTriggerCollection = async () => {
        setLoadingTrigger(true);
        try {
            const res = await fetch('/api/v1/intelligence/trigger', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (res.ok) {
                message.success('采集任务已触发，请稍后刷新查看结果');
            }

        } catch (error) {
            console.error('Trigger failed:', error);
        } finally {
            setLoadingTrigger(false);
        }
    };

    // 批准入库操作
    const handleApprove = async (id: string, data: any) => {
        try {
            const res = await fetch(`/api/v1/intelligence/reviews/${id}/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'APPROVE', correction_data: data })
            });
            if (res.ok) {
                // 操作成功后，从列表中移除该条目
                setReviews(reviews.filter(r => r.id !== id));
            }
        } catch (error) {
            console.error('Approve failed:', error);
        }
    };

    // 拒绝/忽略操作
    const handleReject = async (id: string) => {
        try {
            const res = await fetch(`/api/v1/intelligence/reviews/${id}/reject`, {
                method: 'POST'
            });
            if (res.ok) {
                // 操作成功后，从列表中移除该条目
                setReviews(reviews.filter(r => r.id !== id));
            }
        } catch (error) {
            console.error('Reject failed:', error);
        }
    };

    // 监听筛选条件变化，自动刷新列表
    useEffect(() => {
        fetchReviews();
    }, [filterFocus, statusFilter]);

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#2d3748', margin: 0 }}>数据情报室</h1>
                    <p style={{ color: '#718096', margin: '4px 0 0' }}>AI 采集结果审核与入库</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={filterFocus}
                            onChange={(e) => setFilterFocus(e.target.checked)}
                        />
                        <span style={{ color: filterFocus ? '#b7791f' : '#4a5568', fontWeight: filterFocus ? 'bold' : 'normal' }}>
                            只看承德专线
                        </span>
                    </label>
                    <select
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                    >
                        <option value="PENDING">待审核</option>
                        <option value="APPROVED">已通过</option>
                        <option value="REJECTED">已拒绝</option>
                        <option value="ALL">全部</option>
                    </select>

                    <button
                        onClick={() => setUploadModalVisible(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#ed8936',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        📄 上传情报
                    </button>

                    <button
                        onClick={handleTriggerCollection}
                        disabled={loadingTrigger}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: loadingTrigger ? '#a0aec0' : '#3182ce',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loadingTrigger ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {loadingTrigger ? '触发中...' : '手动触发采集'}
                    </button>
                    <button
                        onClick={fetchReviews}
                        style={{ padding: '8px 16px', backgroundColor: 'white', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        刷新
                    </button>
                </div>
            </div>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <div className="review-list">
                    {reviews.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#a0aec0', backgroundColor: 'white', borderRadius: '8px' }}>
                            暂无待审核数据
                        </div>
                    ) : (
                        reviews.map(item => (
                            <ReviewCard
                                key={item.id}
                                data={item}
                                onApprove={handleApprove}
                                onReject={handleReject}
                            />
                        ))
                    )}
                </div>
            )}

            <UploadModal
                visible={uploadModalVisible}
                onClose={() => setUploadModalVisible(false)}
                onSuccess={() => {
                    fetchReviews();
                }}
            />
        </div>
    );
};

export default IntelligenceRoom;
